/**
 * Shared customer-commerce offer freshness and eligibility.
 *
 * CURRENT / VERIFIED uses a single 48-hour window on last_success_at
 * (legacy fallback: last_checked_at). Do not use updated_at or query time.
 */

import {
  formatHeroCurrency,
  getComparableOfferPrice,
  getOfferLink,
  getRetailerNameFromOffer,
  pricesMatch,
} from '../components/PriceComparison/simpleProductPageShared';
import { compareRetailerPreference } from './retailerPreference';

export const OFFER_CURRENT_WINDOW_HOURS = 48;
export const OFFER_CURRENT_WINDOW_MS =
  OFFER_CURRENT_WINDOW_HOURS * 60 * 60 * 1000;

export const OFFER_FRESHNESS_STATE = Object.freeze({
  CURRENT: 'CURRENT',
  STALE: 'STALE',
  UNAVAILABLE: 'UNAVAILABLE',
  UNKNOWN: 'UNKNOWN',
});

const EXPLICIT_UNAVAILABLE = new Set([
  'unavailable',
  'out of stock',
  'out-of-stock',
  'out_of_stock',
  'outofstock',
  'sold out',
  'sold-out',
  'sold_out',
  'not available',
  'not-available',
  'preorder',
  'backorder',
]);

function resolveNow(now) {
  if (now instanceof Date) return now.getTime();
  if (typeof now === 'number' && Number.isFinite(now)) return now;
  return Date.now();
}

function parseTimestamp(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

function getRawAvailability(offer) {
  const status =
    offer?.availability_status || offer?.availability || offer?.Availability;
  if (!status || typeof status !== 'string') return null;
  const normalized = status.trim();
  if (!normalized) return null;
  return normalized;
}

export function isExplicitlyUnavailable(offer) {
  const raw = getRawAvailability(offer);
  if (!raw) return false;
  return EXPLICIT_UNAVAILABLE.has(raw.toLowerCase());
}

export function getOfferVerificationTimestamp(offer) {
  if (!offer) return null;
  const success = parseTimestamp(offer.last_success_at);
  if (success != null) return success;
  const checked = parseTimestamp(offer.last_checked_at);
  if (checked != null) return checked;
  return parseTimestamp(offer['Date Time Stamp']);
}

export function getOfferFreshnessState(offer, now) {
  if (!offer) return OFFER_FRESHNESS_STATE.UNKNOWN;
  if (isExplicitlyUnavailable(offer)) return OFFER_FRESHNESS_STATE.UNAVAILABLE;
  const verifiedAt = getOfferVerificationTimestamp(offer);
  if (verifiedAt == null) return OFFER_FRESHNESS_STATE.UNKNOWN;
  const age = resolveNow(now) - verifiedAt;
  if (age <= OFFER_CURRENT_WINDOW_MS) return OFFER_FRESHNESS_STATE.CURRENT;
  return OFFER_FRESHNESS_STATE.STALE;
}

export function isCurrentVerifiedOffer(offer, now) {
  return getOfferFreshnessState(offer, now) === OFFER_FRESHNESS_STATE.CURRENT;
}

export function hasUsableOfferUrl(offer) {
  const href = getOfferLink(offer);
  if (!href || typeof href !== 'string') return false;
  const trimmed = href.trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed, 'https://example.com');
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function isPurchasableOffer(offer, options = {}) {
  if (!offer || offer.is_active === false) return false;
  if (getComparableOfferPrice(offer, options) == null) return false;
  if (isExplicitlyUnavailable(offer)) return false;
  return hasUsableOfferUrl(offer);
}

export function isCurrentVerifiedPurchasableOffer(offer, now, options = {}) {
  return isPurchasableOffer(offer, options) && isCurrentVerifiedOffer(offer, now);
}

function compareRetailerName(a, b) {
  return compareRetailerPreference(getRetailerNameFromOffer(a), getRetailerNameFromOffer(b));
}

function compareVerificationNewestFirst(a, b) {
  const timeA = getOfferVerificationTimestamp(a) ?? 0;
  const timeB = getOfferVerificationTimestamp(b) ?? 0;
  return timeB - timeA;
}

function compareCurrentWinner(a, b, options) {
  const priceA = getComparableOfferPrice(a, options) ?? Infinity;
  const priceB = getComparableOfferPrice(b, options) ?? Infinity;
  if (priceA !== priceB) return priceA - priceB;

  const nameCmp = compareRetailerName(a, b);
  if (nameCmp !== 0) return nameCmp;

  const verifiedCmp = compareVerificationNewestFirst(a, b);
  if (verifiedCmp !== 0) return verifiedCmp;

  return getOfferLink(a).localeCompare(getOfferLink(b));
}

function displayGroup(offer, now, options) {
  if (isCurrentVerifiedPurchasableOffer(offer, now, options)) return 0;
  if (isPurchasableOffer(offer, options)) return 1;
  return 2;
}

export function sortCustomerOffers(offers, options = {}) {
  const { now, ...priceOptions } = options;
  const list = (Array.isArray(offers) ? offers : []).filter(
    (offer) => getComparableOfferPrice(offer, priceOptions) != null
  );
  return [...list].sort((a, b) => {
    const groupA = displayGroup(a, now, priceOptions);
    const groupB = displayGroup(b, now, priceOptions);
    if (groupA !== groupB) return groupA - groupB;
    return compareCurrentWinner(a, b, priceOptions);
  });
}

export function selectCurrentVerifiedBest(offers, options = {}) {
  const { now, ...priceOptions } = options;
  const eligible = (Array.isArray(offers) ? offers : []).filter((offer) =>
    isCurrentVerifiedPurchasableOffer(offer, now, priceOptions)
  );
  const sorted = [...eligible].sort((a, b) =>
    compareCurrentWinner(a, b, priceOptions)
  );

  if (!sorted.length) {
    return {
      primary: null,
      tied: [],
      isTie: false,
      uniqueLowest: false,
      hasCurrent: false,
      lowestPrice: null,
    };
  }

  const lowestPrice = getComparableOfferPrice(sorted[0], priceOptions);
  const tied = sorted.filter((offer) =>
    pricesMatch(getComparableOfferPrice(offer, priceOptions), lowestPrice)
  );
  const isTie = tied.length > 1;

  return {
    primary: tied[0],
    tied,
    isTie,
    uniqueLowest: !isTie,
    hasCurrent: true,
    lowestPrice,
  };
}

export function formatVerificationDate(offer) {
  const ts = getOfferVerificationTimestamp(offer);
  if (ts == null) return null;
  return new Date(ts).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatCurrentOfferFreshness(offer) {
  const dateLabel = formatVerificationDate(offer);
  return dateLabel ? `Updated ${dateLabel}` : null;
}

export function formatStaleLastVerifiedLabel(offer, options = {}) {
  const price = formatHeroCurrency(getComparableOfferPrice(offer, options));
  const dateLabel = formatVerificationDate(offer);
  if (price && dateLabel) return `Last verified ${price} on ${dateLabel}`;
  if (dateLabel) return `Last verified on ${dateLabel}`;
  if (price) return `Last verified ${price}`;
  return 'Last verified price';
}

export const STALE_PRICE_DISCLAIMER = 'Price may have changed';

export function formatOfferCtaLabel(offer) {
  const retailer = getRetailerNameFromOffer(offer);
  return `Check price at ${retailer}`;
}

export function getOfferListPresentation(offer, options = {}) {
  const { now, ...priceOptions } = options;
  const state = getOfferFreshnessState(offer, now);
  const availability = getRawAvailability(offer);
  const availabilityLabel =
    availability && availability.toLowerCase() !== 'unknown' ? availability : null;
  const retailer = getRetailerNameFromOffer(offer);
  const priceLabel = formatHeroCurrency(getComparableOfferPrice(offer, priceOptions));
  const dateLabel = formatVerificationDate(offer);

  if (state === OFFER_FRESHNESS_STATE.STALE) {
    return {
      state,
      retailer,
      priceLabel,
      kicker: null,
      meta: formatStaleLastVerifiedLabel(offer, priceOptions),
      disclaimer: STALE_PRICE_DISCLAIMER,
      availabilityLabel,
      ctaLabel: formatOfferCtaLabel(offer),
      isCurrent: false,
    };
  }

  if (state === OFFER_FRESHNESS_STATE.UNAVAILABLE) {
    return {
      state,
      retailer,
      priceLabel,
      kicker: null,
      meta: [availabilityLabel, dateLabel ? `Last verified ${dateLabel}` : null]
        .filter(Boolean)
        .join(' · '),
      disclaimer: dateLabel ? STALE_PRICE_DISCLAIMER : null,
      availabilityLabel,
      ctaLabel: formatOfferCtaLabel(offer),
      isCurrent: false,
    };
  }

  if (state === OFFER_FRESHNESS_STATE.UNKNOWN) {
    return {
      state,
      retailer,
      priceLabel,
      kicker: null,
      meta: availabilityLabel,
      disclaimer: null,
      availabilityLabel,
      ctaLabel: formatOfferCtaLabel(offer),
      isCurrent: false,
    };
  }

  return {
    state,
    retailer,
    priceLabel,
    kicker: null,
    meta: [availabilityLabel, dateLabel ? `Updated ${dateLabel}` : null]
      .filter(Boolean)
      .join(' · '),
    disclaimer: null,
    availabilityLabel,
    ctaLabel: formatOfferCtaLabel(offer),
    isCurrent: true,
  };
}

export function getCurrentOfferHeadline(selection) {
  if (!selection?.hasCurrent || !selection.primary) {
    return {
      kicker: 'Current price verification delayed',
      note: null,
      ctaLabel: 'See retailer offers',
      uniqueLowest: false,
      isTie: false,
    };
  }

  const retailer = getRetailerNameFromOffer(selection.primary);
  if (selection.isTie) {
    return {
      kicker: 'Current verified price',
      note: `Same verified price at ${selection.tied.length} retailers`,
      ctaLabel: `Buy at ${retailer}`,
      uniqueLowest: false,
      isTie: true,
    };
  }

  return {
    kicker: 'Best current verified offer',
    note: `Current best offer at ${retailer}`,
    ctaLabel: `Buy at ${retailer}`,
    uniqueLowest: true,
    isTie: false,
  };
}

export function getPriceHubFromDisplay(lowestPriceData) {
  if (!lowestPriceData) return { kind: 'loading' };
  const price = lowestPriceData.price ?? lowestPriceData.lowestPrice;
  if (
    lowestPriceData.currentVerified &&
    typeof price === 'number' &&
    Number.isFinite(price) &&
    price > 0
  ) {
    return { kind: 'from_price', price };
  }
  return { kind: 'unavailable' };
}

export {
  getComparableOfferPrice,
  getOfferLink,
  getRetailerNameFromOffer,
  formatHeroCurrency,
};
